<template>
  <v-app id="inspire">
    <v-main class="grey lighten-4">
      <v-container>
        <v-row>
          
          <!-- 0. SECCIÓN DE GESTIÓN DE CLIENTES (PÚRPURA) -->
          <v-col cols="12">
            <v-card class="elevation-2 rounded-xl mb-6 overflow-hidden border-purple">
              <v-toolbar flat color="white" @click="showClients = !showClients" class="cursor-pointer">
                <v-icon :color="showClients ? 'purple darken-2' : 'grey'" class="mr-2">mdi-office-building-cog</v-icon>
                <v-toolbar-title class="font-weight-bold grey--text text--darken-3">Gestión de Clientes y Módulos</v-toolbar-title>
                <v-spacer></v-spacer>
                
                <!-- Buscador de Clientes -->
                <v-text-field 
                  v-if="showClients" 
                  v-model="searchClients" 
                  append-icon="mdi-magnify" 
                  label="Buscar cliente..." 
                  single-line hide-details outlined dense 
                  class="rounded-lg mr-4" 
                  style="max-width: 300px" 
                  @click.stop
                ></v-text-field>

                <v-btn icon @click.stop="showClients = !showClients">
                  <v-icon>{{ showClients ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </v-toolbar>

              <v-expand-transition>
                <div v-show="showClients">
                  <v-divider></v-divider>
                  <v-data-table 
                    :headers="headersClients" 
                    :items="clientes" 
                    :search="searchClients"
                    :loading="loading" 
                    class="pa-2"
                  >
                    <template v-slot:item.nombre="{ item }">
                      <span class="font-weight-bold purple--text text--darken-4">{{ item.nombre }}</span>
                    </template>
                    <template v-slot:item.modulos="{ item }">
                      <div class="d-flex flex-wrap gap-1">
                        <v-chip v-for="m in item.modulos" :key="m.id" x-small color="purple lighten-5" purple--text class="mr-1 mb-1">
                          {{ m.nombre }}
                        </v-chip>
                        <span v-if="!item.modulos || item.modulos.length === 0" class="grey--text caption italic">Sin módulos activos</span>
                      </div>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon small color="purple" v-bind="attrs" v-on="on" @click.stop="openClientForm('edit', item)">
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Gestionar Módulos</span>
                      </v-tooltip>
                    </template>
                  </v-data-table>
                </div>
              </v-expand-transition>
            </v-card>
          </v-col>

          <!-- 1. SECCIÓN DE USUARIOS (AZUL) -->
          <v-col cols="12">
            <v-card class="elevation-2 rounded-xl mb-6 overflow-hidden border-blue">
              <v-toolbar flat color="white" @click="showUsers = !showUsers" class="cursor-pointer">
                <v-icon :color="showUsers ? 'blue darken-2' : 'grey'" class="mr-2">mdi-account-group</v-icon>
                <v-toolbar-title class="font-weight-bold grey--text text--darken-3">Gestión de Usuarios</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-text-field v-if="showUsers" v-model="search" append-icon="mdi-magnify" label="Buscar usuario..." single-line hide-details outlined dense class="rounded-lg mr-4" style="max-width: 250px" @click.stop></v-text-field>
                <v-btn  v-if="showUsers" color="blue darken-2" dark class="rounded-lg px-4 mr-2" depressed @click.stop="openForm('create')">
                  <v-icon left>mdi-account-plus</v-icon> <span class="hidden-sm-and-down">Nuevo Usuario</span>
                </v-btn>
                <v-btn icon @click.stop="showUsers = !showUsers"><v-icon>{{ showUsers ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn>
              </v-toolbar>
              <v-expand-transition>
                <div v-show="showUsers">
                  <v-divider></v-divider>
                  <v-data-table :headers="headers" :items="usuarios" :search="search" :loading="loading" class="pa-2">
                    <template v-slot:item.username="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar color="blue lighten-4" size="32" class="mr-3">
                          <span class="blue--text font-weight-bold caption">{{ item.username.charAt(0).toUpperCase() }}</span>
                        </v-avatar>
                        <span class="font-weight-medium grey--text text--darken-3">{{ item.username }}</span>
                      </div>
                    </template>
                    
                    <template v-slot:item.id_cliente="{ item }">
                      <span class="text-caption font-weight-bold grey--text text--darken-1">
                        {{ getClientName(item) }}
                      </span>
                    </template>

                    <template v-slot:item.rol="{ item }">
                      <div class="d-flex align-center flex-wrap" style="gap: 4px;">
                        <!-- Iteramos sobre los roles (asegurando que sea un array) -->
                        <v-chip
                          v-for="r in (Array.isArray(item.rol) ? item.rol.slice(0, 2) : [item.rol])"
                          :key="typeof r === 'object' ? r.id : r"
                          small
                          label
                          depressed
                          color="indigo darken-2"
                          dark
                          class="font-weight-black elevation-2 px-2"
                          style="text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; border-left: 3px solid #C5CAE9;"
                        >
                          <v-icon x-small left>mdi-shield-check</v-icon>
                          {{ typeof r === 'object' ? r.nombre : r }}
                        </v-chip>

                        <!-- Indicador "Perrón" de más roles con Tooltip -->
                        <v-tooltip 
                          v-if="Array.isArray(item.rol) && item.rol.length > 2" 
                          bottom 
                          color="indigo darken-4"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-avatar
                              v-bind="attrs"
                              v-on="on"
                              color="indigo lighten-4"
                              size="22"
                              class="indigo--text text--darken-4 font-weight-bold cursor-pointer elevation-1"
                              style="font-size: 10px; border: 1px solid #303F9F"
                            >
                              +{{ item.rol.length - 2 }}
                            </v-avatar>
                          </template>
                          <div class="pa-2">
                            <div class="text-overline mb-1 border-bottom">Roles Adicionales</div>
                            <div v-for="r in item.rol.slice(2)" :key="r.id" class="d-flex align-center mb-1">
                              <v-icon x-small color="white" class="mr-2">mdi-shield-star</v-icon>
                              <span class="text-caption">{{ typeof r === 'object' ? r.nombre : r }}</span>
                            </div>
                          </div>
                        </v-tooltip>

                        <!-- Caso: Usuario sin roles -->
                        <v-chip v-if="!item.rol || item.rol.length === 0" x-small outlined color="grey">
                          SIN ROL
                        </v-chip>
                      </div>
                    </template>

                    <template v-slot:item.permisos="{ item }">
                      <div class="d-flex align-center">
                        <v-chip
                          v-for="p in item.permisos.slice(0, 2)"
                          :key="p.id"
                          x-small
                          color="blue lighten-5"
                          class="blue--text text--darken-4 mr-1 font-weight-medium"
                        >
                          {{ p.nombre }}
                        </v-chip>

                        <v-tooltip bottom v-if="item.permisos.length > 2" color="grey darken-4">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              x-small
                              class="grey lighten-3 grey--text text--darken-2 font-weight-bold"
                            >
                              +{{ item.permisos.length - 2 }}
                            </v-chip>
                          </template>
                          <!-- Contenido del Tooltip al pasar el ratón -->
                          <div class="pa-1">
                            <div v-for="p in item.permisos.slice(2)" :key="p.id" class="text-caption">
                              • {{ p.nombre }}
                            </div>
                          </div>
                        </v-tooltip>

                        <!-- Caso: Sin permisos -->
                        <span v-if="!item.permisos || item.permisos.length === 0" class="grey--text caption italic">
                          Sin permisos
                        </span>
                      </div>
                    </template>

                    <template v-slot:item.actions="{ item }">
                      <v-btn icon small color="blue darken-1" @click.stop="openForm('edit', item)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon small color="red lighten-1" @click.stop="deleteUser(item)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>

                  </v-data-table>
                </div>
              </v-expand-transition>
            </v-card>
          </v-col>

          <!-- 2. SECCIÓN DE ROLES (NARANJA) -->
          <v-col cols="12">
            <v-card class="elevation-2 rounded-xl mb-6 overflow-hidden border-orange">
              <v-toolbar flat color="white" @click="showRoles = !showRoles" class="cursor-pointer">
                <v-icon :color="showRoles ? 'orange darken-2' : 'grey'" class="mr-2">mdi-shield-account</v-icon>
                <v-toolbar-title class="font-weight-bold grey--text text--darken-3">Configuración de Roles</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn v-if="showRoles" color="orange darken-2" dark class="rounded-lg px-4 mr-2" depressed @click.stop="openRoleForm('create')">
                  <v-icon left>mdi-shield-plus</v-icon> <span class="hidden-sm-and-down">Nuevo Rol</span>
                </v-btn>
                <v-btn icon @click.stop="showRoles = !showRoles"><v-icon>{{ showRoles ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn>
              </v-toolbar>
              <v-expand-transition>
                <div v-show="showRoles">
                  <v-divider></v-divider>
                  <v-data-table :headers="headersRoles" :items="roles" :loading="loading" class="pa-2">
                    <template v-slot:item.permisos="{ item }">
                      <v-chip v-for="pId in item.permisos" :key="pId" x-small color="orange lighten-4" class="mr-1 mb-1">{{ getPermisoNombre(pId) }}</v-chip>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-btn icon small color="blue" @click.stop="openRoleForm('edit', item)"><v-icon small>mdi-pencil</v-icon></v-btn>
                      <v-btn icon small color="red" @click.stop="deleteRole(item)"><v-icon small>mdi-delete</v-icon></v-btn>
                    </template>
                  </v-data-table>
                </div>
              </v-expand-transition>
            </v-card>
          </v-col>

          <!-- 4. SECCIÓN DE MÓDULOS (ÍNDIGO) -->
          <v-col cols="12">
            <v-card class="elevation-2 rounded-xl overflow-hidden border-indigo">
              <v-toolbar flat color="white" @click="showModules = !showModules" class="cursor-pointer">
                <v-icon :color="showModules ? 'indigo darken-1' : 'grey'" class="mr-2">mdi-apps</v-icon>
                <v-toolbar-title class="font-weight-bold grey--text text--darken-3">Módulos del Sistema</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn v-if="showModules" color="indigo darken-1" dark class="rounded-lg px-4 mr-2" depressed @click.stop="openModuloForm('create')">
                  <v-icon left>mdi-plus-box</v-icon> <span class="hidden-sm-and-down">Nuevo Módulo</span>
                </v-btn>
                <v-btn icon @click.stop="showModules = !showModules"><v-icon>{{ showModules ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn>
              </v-toolbar>
              <v-expand-transition>
                <div v-show="showModules">
                  <v-divider></v-divider>
                  <v-data-table :headers="headersModulos" :items="modulosApp" :loading="loading" class="pa-2">
                    <template v-slot:item.nombre="{ item }">
                      <span class="font-weight-bold indigo--text">{{ item.nombre }}</span>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-btn icon small color="blue" @click.stop="openModuloForm('edit', item)"><v-icon small>mdi-pencil</v-icon></v-btn>
                      <v-btn icon small color="red" @click.stop="deleteModulo(item)"><v-icon small>mdi-delete</v-icon></v-btn>
                    </template>
                  </v-data-table>
                </div>
              </v-expand-transition>
            </v-card>
          </v-col>

          <!-- 3. SECCIÓN DE PERMISOS (VERDE) -->
          <v-col cols="12">
            <v-card class="elevation-2 rounded-xl overflow-hidden border-green">
              <v-toolbar flat color="white" @click="showPerms = !showPerms" class="cursor-pointer">
                <v-icon :color="showPerms ? 'teal darken-1' : 'grey'" class="mr-2">mdi-key-variant</v-icon>
                <v-toolbar-title class="font-weight-bold grey--text text--darken-3">Diccionario de Permisos</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn v-if="showPerms" color="teal darken-1" dark class="rounded-lg px-4 mr-2" depressed @click.stop="openPermForm('create')">
                  <v-icon left>mdi-plus-box</v-icon> <span class="hidden-sm-and-down">Nuevo Permiso</span>
                </v-btn>
                <v-btn icon @click.stop="showPerms = !showPerms"><v-icon>{{ showPerms ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn>
              </v-toolbar>
              <v-expand-transition>
                <div v-show="showPerms">
                  <v-divider></v-divider>
                  <v-data-table :headers="headersPerms" :items="permisos" :loading="loading" class="pa-2">
                    <template v-slot:item.nombre="{ item }">
                      <code class="teal--text font-weight-bold">{{ item.nombre }}</code>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-btn icon small color="blue" @click.stop="openPermForm('edit', item)"><v-icon small>mdi-pencil</v-icon></v-btn>
                      <v-btn icon small color="red" @click.stop="deletePerm(item)"><v-icon small>mdi-delete</v-icon></v-btn>
                    </template>
                  </v-data-table>
                </div>
              </v-expand-transition>
            </v-card>
          </v-col>
        </v-row>

        <!-- --- DIÁLOGOS --- -->

        <!-- DIÁLOGO CLIENTE (SOLO EDICIÓN DE MÓDULOS) -->
        <v-dialog v-model="clientDialog" max-width="500px" persistent>
          <v-card class="rounded-xl">
            <v-card-title class="purple darken-2 white--text py-4">
              <v-icon left color="white">mdi-office-building</v-icon>
              <span class="text-h6">Gestionar Módulos de Cliente</span>
            </v-card-title>
            <v-card-text class="pa-6">
              <v-form ref="clientForm">
                <v-text-field v-model="clientModel.nombre" label="Nombre del Cliente" outlined dense prepend-inner-icon="mdi-domain" disabled></v-text-field>
                <v-text-field v-model="clientModel.id_cliente" label="ID Cliente" outlined dense prepend-inner-icon="mdi-identifier" disabled></v-text-field>
                <v-select 
                  v-model="clientModel.modulos" :items="modulosApp" item-text="nombre" item-value="id" label="Módulos Permitidos" 
                  multiple chips outlined dense prepend-inner-icon="mdi-apps-box"
                ></v-select>
              </v-form>
            </v-card-text>
            <v-card-actions class="pa-4 grey lighten-5">
              <v-spacer></v-spacer>
              <v-btn text @click="clientDialog = false">Cancelar</v-btn>
              <v-btn color="purple darken-2" dark class="rounded-lg px-8" depressed @click="submitClientForm">Actualizar Módulos</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- DIÁLOGO USUARIO -->
        <v-dialog v-model="formDialog" max-width="600px" persistent>
          <v-card class="rounded-xl">
            <v-card-title class="blue darken-2 white--text py-4">
              <v-icon left color="white">{{ formMode === 'create' ? 'mdi-account-plus' : 'mdi-account-edit' }}</v-icon>
              <span class="text-h6">{{ formMode === 'create' ? 'Nuevo Usuario' : 'Editar Usuario' }}</span>
            </v-card-title>
            <v-card-text class="pa-6">
              <v-form ref="form">
                <v-row dense>
                  <v-col cols="12" sm="6" v-if="formMode === 'create'"><v-text-field v-model="formModel.cif" label="CIF" outlined dense prepend-inner-icon="mdi-domain"></v-text-field></v-col>
                  <v-col cols="12" :sm="formMode === 'create' ? 6 : 12"><v-text-field v-model="formModel.username" label="Usuario" outlined dense prepend-inner-icon="mdi-account"></v-text-field></v-col>
                  <v-col cols="12"><v-text-field v-model="formModel.email" label="Email" outlined dense prepend-inner-icon="mdi-email"></v-text-field></v-col>
                  <v-col cols="12" v-if="formMode === 'create'"><v-text-field v-model="formModel.password" type="password" label="Contraseña" outlined dense prepend-inner-icon="mdi-lock"></v-text-field></v-col>
                  <v-col cols="12"><v-combobox :disabled="formMode != 'create'" v-model="formModel.id_cliente" :items="clientes" item-text="nombre" label="Cliente" @change="getClientModules" outlined dense></v-combobox></v-col>
                  <v-col cols="12" sm="6"><v-select v-model="formModel.rol" :items="filteredRoles" item-text="nombre" item-value="id" label="Roles disponibles" outlined dense multiple chips prepend-inner-icon="mdi-shield-account" @change="handleRolChange"></v-select></v-col>
                  <v-col cols="12" sm="6"><v-select v-model="formModel.permisos" :items="filteredPermisos" item-text="nombre" item-value="id" label="Permisos" multiple chips outlined dense prepend-inner-icon="mdi-key"></v-select></v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions class="pa-4 grey lighten-5">
              <v-spacer></v-spacer>
              <v-btn text @click="formDialog = false">Cancelar</v-btn>
              <v-btn color="blue darken-2" dark class="rounded-lg px-8" depressed @click="submitForm">Guardar Usuario</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- DIÁLOGO ROL -->
        <v-dialog v-model="roleDialog" max-width="500px" persistent>
          <v-card class="rounded-xl">
            <v-card-title class="orange darken-3 white--text py-4"><span class="text-h6">{{ roleFormMode === 'create' ? 'Nuevo Rol' : 'Editar Rol' }}</span></v-card-title>
            <v-card-text class="pa-6">
              <v-form ref="roleForm">
                <v-text-field v-model="roleModel.nombre" label="Nombre del Rol" outlined dense prepend-inner-icon="mdi-badge-account"></v-text-field>
                <v-select v-model="roleModel.permisos" :items="permisos" item-text="nombre" item-value="id" label="Permisos Heredados" multiple chips outlined dense prepend-inner-icon="mdi-list-status"></v-select>
              </v-form>
            </v-card-text>
            <v-card-actions class="pa-4 grey lighten-5">
              <v-spacer></v-spacer>
              <v-btn text @click="roleDialog = false">Cancelar</v-btn>
              <v-btn color="orange darken-3" dark class="rounded-lg px-8" depressed @click="submitRoleForm">Guardar Rol</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- DIÁLOGO PERMISO -->
        <v-dialog v-model="permDialog" max-width="400px" persistent>
          <v-card class="rounded-xl">
            <v-card-title class="teal darken-1 white--text py-4"><span class="text-h6">{{ permFormMode === 'create' ? 'Nuevo Permiso' : 'Editar Permiso' }}</span></v-card-title>
            <v-card-text class="pa-6">
              <v-form ref="permForm">
                <v-text-field v-model="permModel.nombre" label="Clave del Permiso" outlined dense prepend-inner-icon="mdi-key-plus"></v-text-field>
              </v-form>
            </v-card-text>
            <v-card-actions class="pa-4 grey lighten-5">
              <v-spacer></v-spacer>
              <v-btn text @click="permDialog = false">Cancelar</v-btn>
              <v-btn color="teal darken-1" dark class="rounded-lg px-8" depressed @click="submitPermForm">Guardar Permiso</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- DIÁLOGO MÓDULO -->
        <v-dialog v-model="moduloDialog" max-width="400px" persistent>
          <v-card class="rounded-xl">
            <v-card-title class="indigo darken-1 white--text py-4">
              <span class="text-h6">{{ moduloFormMode === 'create' ? 'Nuevo Módulo' : 'Editar Módulo' }}</span>
            </v-card-title>
            <v-card-text class="pa-6">
              <v-form ref="moduloForm">
                <v-text-field v-model="moduloModel.nombre" label="Nombre del Módulo" outlined dense prepend-inner-icon="mdi-package-variant-closed"></v-text-field>
              </v-form>
            </v-card-text>
            <v-card-actions class="pa-4 grey lighten-5">
              <v-spacer></v-spacer>
              <v-btn text @click="moduloDialog = false">Cancelar</v-btn>
              <v-btn color="indigo darken-1" dark class="rounded-lg px-8" depressed @click="submitModuloForm">Guardar Módulo</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Alerta Errores -->
        <v-dialog v-model="dialog" max-width="400px">
          <v-alert type="error" prominent border="left" class="mb-0">
            {{ error_text }}
            <v-divider class="my-3"></v-divider>
            <v-btn text small @click="dialog = false">Cerrar</v-btn>
          </v-alert>
        </v-dialog>

      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { mapActions } from "vuex";

export default {
  data() {
    return {
      // Visibilidad paneles
      showClients: false,
      showUsers: false,
      showRoles: false,
      showPerms: false,
      showModules: false,

      // Estados Diálogos
      clientDialog: false,
      formDialog: false, formMode: "create",
      roleDialog: false, roleFormMode: "create",
      permDialog: false, permFormMode: "create",
      moduloDialog: false, moduloFormMode: "create",
      
      loading: false, 
      searchClients: "", 
      search: "", 
      dialog: false, 
      error_text: "",

      // Modelos
      clientModel: { id_cliente: null, nombre: "", modulos: [] },
      formModel: { id: null, cif: "", username: "", password: "", email: "", rol: "", id_cliente: null, permisos: [] },
      roleModel: { id: null, nombre: "", permisos: [] },
      permModel: { id: null, nombre: "" },
      moduloModel: { id: null, nombre: "" },

      // Listas
      usuarios: [], roles: [], permisos: [], clientes: [],
      modulosApp: [],

      // Headers Tablas
      headersClients: [
        { text: "ID", value: "id_cliente" },
        { text: "Nombre Cliente", value: "nombre", class: "purple lighten-5 purple--text" },
        { text: "Módulos Activos", value: "modulos", sortable: false },
        { text: "Acciones", value: "actions", sortable: false, align: "end" },
      ],
      headers: [
        { text: "Usuario", value: "username", class: "blue lighten-5 blue--text" },
        { text: "Cliente Asignado", value: "id_cliente" },
        { text: "Email", value: "email" },
        { text: "Rol", value: "rol" },
        { text: "Permisos Extra", value: "permisos", sortable: false },
        { text: "Acciones", value: "actions", sortable: false, align: "end" },
      ],
      headersRoles: [
        { text: "Rol", value: "nombre", class: "orange lighten-5 orange--text" },
        { text: "Permisos Base", value: "permisos", sortable: false },
        { text: "Acciones", value: "actions", sortable: false, align: "end" },
      ],
      headersPerms: [
        { text: "Nombre Permiso", value: "nombre", class: "teal lighten-5 teal--text" },
        { text: "Acciones", value: "actions", sortable: false, align: "end" },
      ],
      headersModulos: [
        { text: "Nombre Módulo", value: "nombre", class: "indigo lighten-5 indigo--text" },
        { text: "Acciones", value: "actions", sortable: false, align: "end" },
      ],
    };
  },

  computed: {
    // 1. Auxiliar para no repetir código: Obtiene los nombres de los módulos en mayúsculas
    nombresModulosCliente() {
      if (!this.formModel.id_cliente) return [];

      const clientId = typeof this.formModel.id_cliente === 'object' 
                      ? this.formModel.id_cliente.id_cliente 
                      : this.formModel.id_cliente;

      const cliente = this.clientes.find(c => c.id_cliente == clientId);
      return cliente?.modulos ? cliente.modulos.map(m => m.nombre.toUpperCase()) : [];
    },

    // 2. Roles filtrados por los módulos del cliente
    filteredRoles() {
      const modulos = this.nombresModulosCliente;
      return this.roles.filter(role => 
        modulos.includes(role.nombre.toUpperCase()) || role.nombre.toUpperCase() === 'DEVELOPER'
      );
    },

    // 3. Todos los permisos que pertenecen a esos roles filtrados
    filteredPermisos() {
      const rolesFiltrados = this.filteredRoles;
      if (rolesFiltrados.length === 0) return [];

      // Usamos flatMap para aplanar todos los arrays de permisos en uno solo
      const todosLosPermisos = rolesFiltrados.flatMap(rol => rol.permisos);

      const idsUnicos = [...new Set(todosLosPermisos)];

      // 2. BUSCAMOS LOS OBJETOS COMPLETOS en la lista maestra 'permisos'
      // Comparamos los IDs únicos con los IDs de los objetos en this.permisos
      return this.permisos.filter(p => idsUnicos.includes(p.id));
    }
  },

  async created() {
    this.loading = true;
    await Promise.all([this.loadUsuarios(), this.loadClientes(), this.loadPermisos()]);
    this.loading = false;
  },
  methods: {
    ...mapActions(["postData", "getData", "postDataWithReturn"]),

    // --- AUXILIARES ---
    getPermisoNombre(id) {
      const p = this.permisos.find(p => p.id === id);
      return p ? p.nombre : id;
    },
    getClientName(item) {
      if (!item || !item.id_cliente) return '—';
      let idBuscado = item.id_cliente;
      // if (idBuscado == 2000) idBuscado = 11956;
      const c = this.clientes.find(c => c.id_cliente == idBuscado);
      return c ? c.nombre : `ID: ${idBuscado}`;
    },
    getClientModules(){
      const cliente = this.formModel.id_cliente
      if (!cliente) return;
      // if (cliente.id_cliente == 2000) cliente.id_cliente = 11956;
      // const modulosCliente = this.clientes.find((c) => c.id_cliente == cliente.id_cliente)?.modulos.map((m) => m.nombre);
      // this.formModel.permisos = this.permisos
      //   .filter(p => modulosCliente.includes(p.nombre)).map((p) => p.id);
      this.formModel.rol = []; 
      this.formModel.permisos = []; 
    },
    handleRolChange(rolesSeleccionados) {
      // 1. Identificar todos los permisos que pertenecen a CUALQUIER rol
      // (Esto sirve para saber cuáles son gestionados por el sistema de roles)
      const idsPermisosDeTodosLosRoles = this.roles.flatMap(r => r.permisos);

      // 2. Limpiar la lista actual: Mantener solo lo que NO sea de un rol (permisos manuales)
      let permisosManuales = this.formModel.permisos.filter(id => 
        !idsPermisosDeTodosLosRoles.includes(id)
      );

      // 3. Recopilar todos los permisos de los roles seleccionados actualmente
      let permisosNuevosRoles = [];
      rolesSeleccionados.forEach(rolId => {
        const rol = this.roles.find(r => r.id === rolId);
        if (rol && rol.permisos) {
          permisosNuevosRoles.push(...rol.permisos);
        }
      });

      // 4. Unir manuales + nuevos roles y eliminar duplicados con Set
      this.formModel.permisos = [...new Set([...permisosManuales, ...permisosNuevosRoles])];
    },

    // --- CARGA DE DATOS ---
    async loadUsuarios() { const res = await this.getData({ path: "django/portal/get_all_users" }); this.usuarios = res.users || res; },
    async loadClientes() { 
      const res = await this.postDataWithReturn({ path: "django/portal/clientes/" }); 
      this.clientes = res.clientes || res; 
      // let ramosCliente = this.clientes.find(c => c.id_cliente == 11956);
      // ramosCliente.id_cliente = 2000;
    },
    async loadPermisos() { 
      const res = await this.getData({ path: "django/portal/get_permissions/" }); 
      this.permisos = res.permisos_disponibles || []; 
      this.roles = res.roles || []; 
      this.modulosApp = res.modulos || [];
    },

    // --- CRUD CLIENTES (EDIT ONLY) ---
    openClientForm(mode, client = null) {
      this.clientModel = { ...client };
      this.clientDialog = true;
    },
    async submitClientForm() {
      try {
        // this.clientModel.id_cliente = this.clientModel.id_cliente != 11956 ? this.clientModel.id_cliente : 2000; 
        if (this.clientModel.modulos) {
          this.clientModel.modulos = this.clientModel.modulos.map(m => 
            (m && typeof m === 'object') ? m.id : m
          );
        }
        await this.postData({ path: "django/portal/link-modulo/", body: this.clientModel });
        this.clientDialog = false; await this.loadClientes();
      } catch (e) { this.showError("Error en cliente"); }
    },

    // --- CRUD USUARIOS ---
    openForm(mode, user = null) {
      this.formMode = mode;
      if (mode === "create") { this.formModel = { cif: "", username: "", password: "", email: "", rol: "", id_cliente: null, permisos: [] }; }
      else { 
        //recogemos tambien el nombre del cliente para mostrar en la edicion
        const clientObject = this.clientes.find((c) => c.id_cliente == user.id_cliente);

        this.formModel = { 
          ...user,
          rol: Array.isArray(user.rol) ? user.rol : (user.rol ? [user.rol.id || user.rol] : []),
          permisos: user.permisos?.map((p) => p.id) ,
          id_cliente: clientObject
        }; 
      }
      this.formDialog = true;
    },
    async submitForm() {
      try {
        const body = { ...this.formModel };
        if (body.id_cliente && typeof body.id_cliente === 'object') body.id_cliente = body.id_cliente.id_cliente;
        if (body.rol && typeof body.rol[0] === 'object') body.rol = body.rol.map((r) => r.id);
        await this.postData({ path: this.formMode === "create" ? "django/portal/crear-user/" : "django/portal/edit-user/", body });
        this.formDialog = false; await this.loadUsuarios();
      } catch (e) { this.showError("Error en usuario"); }
    },

    // --- CRUD ROLES ---
    openRoleForm(mode, role = null) {
      this.roleFormMode = mode;
      this.roleModel = mode === "create" ? { id: null, nombre: "", permisos: [] } : { ...role };
      this.roleDialog = true;
    },
    async submitRoleForm() {
      try {
        await this.postData({ path: this.roleFormMode === "create" ? "django/portal/crear-rol/" : "django/portal/edit-rol/", body: this.roleModel });
        this.roleDialog = false; await this.loadPermisos();
      } catch (e) { this.showError("Error en rol"); }
    },
    async deleteRole(role) {
      if (!(await this.$confirmar({ titulo: "Eliminar rol", texto: `Se eliminará el rol "${role.nombre}".`, peligro: true }))) return;
      await this.postData({ path: "django/portal/delete-rol/", body: { id: role.id } });
      await this.loadPermisos();
    },

    // --- CRUD MÓDULOS ---
    openModuloForm(mode, modulo = null) {
      this.moduloFormMode = mode;
      this.moduloModel = mode === "create" ? { id: null, nombre: "" } : { ...modulo, nombre_antiguo: modulo.nombre };
      this.moduloDialog = true;
    },

    async submitModuloForm() {
      try {
        const path = this.moduloFormMode === "create" 
          ? "django/portal/crear-modulo/" 
          : "django/portal/edit-modulo/";
        const id_permiso = this.moduloFormMode === "create" ? null : this.permisos.find((p) => p.nombre == this.moduloModel.nombre_antiguo)?.id;
        this.moduloModel.id_permiso = id_permiso;
        await this.postData({ path: path, body: this.moduloModel });
        this.moduloDialog = false;
        await this.loadPermisos(); // Recargamos ya que loadPermisos trae los modulosApp
      } catch (e) { 
        this.showError("Error al guardar el módulo"); 
      }
    },

    async deleteModulo(modulo) {
      if (!(await this.$confirmar({ titulo: "Eliminar módulo", texto: `Se eliminará el módulo "${modulo.nombre}".`, peligro: true }))) return;
      try {
        const id_permiso = this.permisos.find((p) => p.nombre == modulo.nombre)?.id;
        await this.postData({ 
          path: "django/portal/delete-modulo/", 
          body: { id: modulo.id, id_permiso: id_permiso } 
        });
        await this.loadPermisos();
      } catch (e) {
        this.showError("Error al eliminar el módulo");
      }
    },

    // --- CRUD PERMISOS ---
    openPermForm(mode, perm = null) {
      this.permFormMode = mode;
      this.permModel = mode === "create" ? { id: null, nombre: "" } : { ...perm };
      this.permDialog = true;
    },
    async submitPermForm() {
      try {
        await this.postData({ path: this.permFormMode === "create" ? "django/portal/crear-permiso/" : "django/portal/edit-permiso/", body: this.permModel });
        this.permDialog = false; await this.loadPermisos();
      } catch (e) { this.showError("Error en permiso"); }
    },
    async deletePerm(perm) {
      if (!(await this.$confirmar({ titulo: "Eliminar permiso", texto: `Se eliminará el permiso "${perm.nombre}".`, peligro: true }))) return;
      await this.postData({ path: "django/portal/delete-permiso/", body: { id: perm.id } });
      await this.loadPermisos();
    },

    async deleteUser(item) {
      if (!(await this.$confirmar({ titulo: "Eliminar usuario", texto: `Se eliminará el usuario "${item.username || item.nombre || ""}".`, peligro: true }))) return;
      await this.postData({ path: "django/portal/delete-user/", body: item });
      await this.loadUsuarios();
    },
    showError(msg) { this.error_text = msg; this.dialog = true; }
  }
};
</script>

<style scoped>
.rounded-xl { border-radius: 16px !important; }
.v-data-table >>> thead th { text-transform: uppercase; font-size: 0.7rem !important; font-weight: bold !important; letter-spacing: 0.5px; }
.v-toolbar { border-bottom: 1px solid #eee !important; }
.cursor-pointer { cursor: pointer; }
.gap-1 { gap: 4px; }
.italic { font-style: italic; }

.cursor-pointer {
  cursor: pointer;
}
/* Efecto de hover sutil para los roles */
.v-chip--label:hover {
  filter: brightness(1.2);
  transition: 0.3s;
}

/* BORDES SUPERIORES PERSONALIZADOS */
.border-purple { border-top: 4px solid #7B1FA2 !important; }
.border-blue   { border-top: 4px solid #1976D2 !important; }
.border-orange { border-top: 4px solid #F57C00 !important; }
.border-green  { border-top: 4px solid #00897B !important; }
.border-indigo { border-top: 4px solid #3F51B5 !important; }
</style>